/* eslint-disable */
import React, { useState } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import {
  User, FileText, ShoppingCart, Wallet, Receipt, Info, ExternalLink, Banknote,
} from "lucide-react";
import { GhanaMoneyDisplay } from "../../helpers/helper";
import { StatusBadge, timeAgo } from "./TicketBadges";

// The member-360 view: everything the co-operative already knows about a
// member, pulled into one panel so an agent can answer "where is my loan?"
// without leaving the ticket. Rendered inline beside the ticket thread and
// full-width on the standalone member page.
//
// Every section degrades to an empty state independently — the consolidated
// /support/members/{id}/360 endpoint may not exist yet, in which case
// GetMember360 falls back to the profile + loan history it can reach and sets
// `partial`.

const TABS = [
  { key: "loans", label: "Loans", icon: <FileText size={14} /> },
  { key: "loan_deductions", label: "Loan Deduction", icon: <Banknote size={14} /> },
  { key: "orders", label: "Mart Orders", icon: <ShoppingCart size={14} /> },
  { key: "funds", label: "Fund Deduction", icon: <Wallet size={14} /> },
  { key: "dues", label: "Dues", icon: <Receipt size={14} /> },
  { key: "tickets", label: "Tickets", icon: <Info size={14} /> },
];

function EmptyRow({ label }) {
  return (
    <p className="text-xs text-slate-400 dark:text-slate-500 py-6 text-center">
      No {label} on record.
    </p>
  );
}

function Row({ title, subtitle, right, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`w-full text-left py-2.5 flex items-center gap-3 ${onClick ? "hover:bg-slate-50 dark:hover:bg-gray-700/40 rounded-lg px-2 transition-colors" : ""}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">{title}</p>
        {subtitle && (
          <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{subtitle}</p>
        )}
      </div>
      {right && <div className="flex-shrink-0">{right}</div>}
    </Tag>
  );
}

// One figure in the profile header strip.
function Stat({ label, children, hint = null, accent = false }) {
  return (
    <div
      className={`px-3 py-2 rounded-lg ${
        accent
          ? "bg-indigo-50 dark:bg-indigo-900/20"
          : "bg-slate-50 dark:bg-gray-900/40"
      }`}
    >
      <p
        className={`text-[10px] font-bold uppercase tracking-wide ${
          accent
            ? "text-indigo-500 dark:text-indigo-400"
            : "text-slate-400 dark:text-slate-500"
        }`}
      >
        {label}
      </p>
      <p
        className={`text-xs font-bold mt-0.5 ${
          accent
            ? "text-indigo-700 dark:text-indigo-300"
            : "text-slate-700 dark:text-slate-200"
        }`}
      >
        {children}
      </p>
      {hint && (
        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 leading-tight">
          {hint}
        </p>
      )}
    </div>
  );
}

function Member360Panel({ member360, memberTickets = [], compact = false }) {
  const history = useHistory();
  const [tab, setTab] = useState("loans");

  const profile = member360?.profile || member360 || {};
  const totals = member360?.totals || {};
  const loans = member360?.loans || [];
  const orders = member360?.orders || [];
  const funds = member360?.funds || [];
  const dues = member360?.dues || [];
  const loanDeductions = member360?.loan_deductions || [];

  const hasMember = !!(profile?.staff_id || profile?.name);

  // `date_joined` is a free-form string on the user record (not a Date), and
  // older rows leave it empty — only render the hint when it actually parses,
  // otherwise fall back to the account's creation date.
  const joined = moment(
    profile?.date_joined || profile?.created_at || null
  );
  const joinedLabel = joined.isValid()
    ? `Joined ${joined.format("DD MMM YYYY")}`
    : null;

  if (!hasMember) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-6 text-center">
        <User size={26} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
        <p className="text-xs text-slate-400 dark:text-slate-500">
          No member linked to this ticket.
        </p>
      </div>
    );
  }

  const activeLoan = loans.find(
    (l) => !["PAID", "CLOSED", "REJECTED"].includes(String(l?.status || "").toUpperCase())
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden">
      {/* Identity header */}
      <div className="p-4 border-b border-slate-100 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <img
            src={profile?.profile_image || "/images/dev/avarta2.jpeg"}
            alt="Member"
            className="w-11 h-11 rounded-full border-2 border-white dark:border-gray-600 shadow-sm"
          />
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
              {profile?.name || "Unknown member"}
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
              {profile?.staff_id ? `Staff ID ${profile.staff_id}` : "—"}
              {profile?.phone ? ` · ${profile.phone}` : ""}
            </p>
          </div>
        </div>

        <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-4"} gap-2 mt-3`}>
          <Stat label="Membership" hint={joinedLabel}>
            {profile?.membership_status || profile?.status || "—"}
          </Stat>

          {/* Lifetime fund contributions. `funds_total` is null (not 0) when the
              consolidated endpoint is unavailable — show "—" rather than imply
              the member has contributed nothing. */}
          <Stat label="Total Funds" accent>
            {totals?.funds_total === null || totals?.funds_total === undefined ? (
              "—"
            ) : (
              <GhanaMoneyDisplay amount={totals.funds_total} />
            )}
          </Stat>

          <Stat label="Loan Balance">
            {totals?.loan_balance !== null && totals?.loan_balance !== undefined ? (
              <GhanaMoneyDisplay amount={totals.loan_balance} />
            ) : activeLoan ? (
              <GhanaMoneyDisplay amount={activeLoan?.balance ?? activeLoan?.amount ?? 0} />
            ) : (
              "None"
            )}
          </Stat>

          <Stat label="Total Dues">
            {totals?.dues_total === null || totals?.dues_total === undefined ? (
              "—"
            ) : (
              <GhanaMoneyDisplay amount={totals.dues_total} />
            )}
          </Stat>
        </div>

        {member360?.partial && (
          <p className="mt-3 text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
            Limited view — the consolidated member endpoint is not available, so
            mart, fund and dues history are not shown.
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-2 pt-2 border-b border-slate-100 dark:border-gray-700 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold rounded-t-lg whitespace-nowrap transition-colors ${
              tab === t.key
                ? "text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400"
                : "text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab body */}
      <div className={`px-3 py-2 ${compact ? "max-h-[320px]" : "max-h-[520px]"} overflow-y-auto divide-y divide-slate-100 dark:divide-gray-700`}>
        {tab === "loans" &&
          (loans.length === 0 ? (
            <EmptyRow label="loans" />
          ) : (
            loans.map((l, i) => (
              <div key={l._id || i}>
                <Row
                  title={<GhanaMoneyDisplay amount={l?.amount ?? 0} />}
                  subtitle={`${l?.loan_type || "Loan"} · ${l?.duration || "—"} months · ${timeAgo(l?.created_at || l?.createdAt)}`}
                  right={<StatusBadge status={l?.status} />}
                />
                {/* Rejection detail is the most-asked support question — show
                    who rejected and why inline rather than behind a click. */}
                {/* Shades <= 900 only: Tailwind here is 3.1.8, which has no
                    `950` (see the note in LoanActivityTrail). */}
                {l?.rejection_reason && (
                  <div className="mb-2.5 ml-2 mr-2 px-2.5 py-1.5 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 rounded-lg">
                    <p className="text-[11px] text-red-800 dark:text-red-200 leading-snug">
                      {l.rejection_reason}
                    </p>
                    <p className="text-[10px] font-bold text-red-600 dark:text-red-400 mt-0.5">
                      Rejected {l?.rejected_by ? `by ${l.rejected_by}` : ""}
                      {l?.rejected_at ? ` · ${timeAgo(Number(l.rejected_at))}` : ""}
                      {l?.edit_requested ? " · edit requested" : ""}
                    </p>
                  </div>
                )}
              </div>
            ))
          ))}

        {tab === "orders" &&
          (orders.length === 0 ? (
            <EmptyRow label="mart orders" />
          ) : (
            orders.map((o, i) => (
              <Row
                key={o._id || i}
                title={o?.product?.name || o?.item_name || "Order"}
                subtitle={`#${o?.order_no || "—"} · ${timeAgo(o?.created_at || o?.createdAt)}`}
                right={<StatusBadge status={o?.status} />}
              />
            ))
          ))}

        {tab === "loan_deductions" &&
          (loanDeductions.length === 0 ? (
            <EmptyRow label="loan deductions" />
          ) : (
            loanDeductions.map((d, i) => (
              <Row
                key={d._id || i}
                title={<GhanaMoneyDisplay amount={d?.amount ?? 0} />}
                subtitle={`${d?.period || "—"} · ${d?.transaction_type || "Loan deduction"}`}
                right={
                  d?.balance != null ? (
                    <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      Bal <GhanaMoneyDisplay amount={d.balance} />
                    </span>
                  ) : null
                }
              />
            ))
          ))}

        {tab === "funds" &&
          (funds.length === 0 ? (
            <EmptyRow label="fund deductions" />
          ) : (
            funds.map((f, i) => (
              <Row
                key={f._id || i}
                title={<GhanaMoneyDisplay amount={f?.amount ?? 0} />}
                subtitle={`${f?.period || "—"} · ${f?.transaction_type || "Contribution"}`}
              />
            ))
          ))}

        {tab === "dues" &&
          (dues.length === 0 ? (
            <EmptyRow label="dues" />
          ) : (
            dues.map((d, i) => (
              <Row
                key={d._id || i}
                title={<GhanaMoneyDisplay amount={d?.amount ?? 0} />}
                subtitle={d?.period || "—"}
              />
            ))
          ))}

        {tab === "tickets" &&
          (memberTickets.length === 0 ? (
            <EmptyRow label="previous tickets" />
          ) : (
            memberTickets.map((t, i) => (
              <Row
                key={t._id || i}
                title={t?.subject || "Untitled ticket"}
                subtitle={`#${t?.ticket_no || "—"} · ${timeAgo(t?.updated_at || t?.updatedAt)}`}
                right={<StatusBadge status={t?.status} />}
                onClick={() =>
                  history.push(`/support/ticket-detailed?id=${t._id || t.ticket_no}`)
                }
              />
            ))
          ))}
      </div>

      {profile?.staff_id && compact && (
        <button
          onClick={() => history.push(`/support/member-detailed?staff_id=${profile.staff_id}`)}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-[11px] font-bold text-indigo-600 dark:text-indigo-400 border-t border-slate-100 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700/40 transition-colors"
        >
          Open full member profile <ExternalLink size={12} />
        </button>
      )}
    </div>
  );
}

export default Member360Panel;
